import { Fragment, type ReactNode } from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { UserOutlined, RobotOutlined, LikeOutlined, DislikeOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { polishAssistantReplyText } from '@/utils/aiAssistantTextSanitize';
import { ToolResultCard } from './ToolResultCard';
import { MemoryReasonTags, type MemoryCitation } from './MemoryReasonTags';
const { Paragraph, Text } = Typography;

/** Tools that render a rich SummaryCard — show the card before the model's short insight. */
const SUMMARIZE_DOC_TOOLS = new Set([
  'summarizeSalesOrder',
  'summarizeWorkOrder',
  'summarizePurchaseOrder',
  'summarizeCustomerAccount',
  'summarizeSupplierAccount',
  'summarizeVATReturn',
  'summarizeTraceabilityChain',
]);

function orderMessagesForDisplay(messages: AgentMessage[]): AgentMessage[] {
  const result: AgentMessage[] = [];
  let i = 0;
  while (i < messages.length) {
    const cur = messages[i];
    const next = messages[i + 1];
    if (
      cur?.role === 'assistant' &&
      next?.role === 'tool' &&
      SUMMARIZE_DOC_TOOLS.has(next.toolName)
    ) {
      result.push(next, cur);
      i += 2;
      continue;
    }
    result.push(cur);
    i += 1;
  }
  return result;
}

/** Renders **bold**, `code` and in-app markdown links `[label](/path)` within a line. */
function formatInline(line: string): ReactNode {
  const parts = line.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g);
  return parts.map((p, idx) => {
    if (p.startsWith('**') && p.endsWith('**') && p.length >= 4) {
      return (
        <Text strong key={idx}>
          {p.slice(2, -2)}
        </Text>
      );
    }
    if (p.startsWith('`') && p.endsWith('`') && p.length >= 2) {
      return (
        <Text code key={idx}>
          {p.slice(1, -1)}
        </Text>
      );
    }
    const link = p.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const [, label, href] = link;
      if (href.startsWith('/') && !href.startsWith('//') && !href.includes('/:')) {
        return (
          <Link key={idx} to={href}>
            {label}
          </Link>
        );
      }
      if (/^https?:\/\//i.test(href)) {
        return (
          <a key={idx} href={href} target="_blank" rel="noreferrer">
            {label}
          </a>
        );
      }
      return <Fragment key={idx}>{label}</Fragment>;
    }
    return <Fragment key={idx}>{p}</Fragment>;
  });
}

const ORDERED_ITEM = /^\s*\d+[.)]\s+(.*)$/;
const BULLET_ITEM = /^\s*[-*•]\s+(.*)$/;
const GUIDE_CITATION = /\s*\[GUIDE:\s*[^\]]+\]/gi;

type ReplyBlock =
  | { kind: 'p'; text: string }
  | { kind: 'ol' | 'ul'; items: string[] };

function toReplyBlocks(polished: string): ReplyBlock[] {
  const blocks: ReplyBlock[] = [];
  for (const raw of polished.split(/\n/)) {
    const line = raw.replace(GUIDE_CITATION, '').trimEnd();
    if (!line.trim()) continue;
    const ol = line.match(ORDERED_ITEM);
    const ul = ol ? null : line.match(BULLET_ITEM);
    const kind = ol ? 'ol' : ul ? 'ul' : 'p';
    const content = (ol?.[1] ?? ul?.[1] ?? line).trim();
    const last = blocks[blocks.length - 1];
    if (kind !== 'p' && last && last.kind === kind) {
      last.items.push(content);
    } else if (kind === 'p') {
      blocks.push({ kind: 'p', text: content });
    } else {
      blocks.push({ kind, items: [content] });
    }
  }
  return blocks;
}

function AssistantReplyBody({ text }: { text: string }) {
  const polished = polishAssistantReplyText(text);
  if (!polished) return <Text type="secondary">…</Text>;
  const blocks = toReplyBlocks(polished);
  return (
    <>
      {blocks.map((block, bi) => {
        const marginBottom = bi < blocks.length - 1 ? 10 : 0;
        if (block.kind === 'p') {
          return (
            <Paragraph key={bi} style={{ marginBottom, marginTop: 0 }}>
              {formatInline(block.text)}
            </Paragraph>
          );
        }
        const ListTag = block.kind;
        return (
          <ListTag key={bi} style={{ marginTop: 0, marginBottom, paddingLeft: 20 }}>
            {block.items.map((item, ii) => (
              <li key={ii} style={{ marginBottom: 4 }}>
                {formatInline(item)}
              </li>
            ))}
          </ListTag>
        );
      })}
    </>
  );
}

export type AgentMessage =
  | { id: string; role: 'user'; text: string }
  | { id: string; role: 'assistant'; text: string; memoryCitations?: MemoryCitation[] }
  | { id: string; role: 'tool'; toolName: string; data: unknown }
  | { id: string; role: 'error'; text: string };

export interface AgentMessagesProps {
  messages: AgentMessage[];
  emptyHint?: string;
  onActionPrompt?: (prompt: string) => void;
  onPin?: (input: {
    sourceType: 'kpi' | 'chart' | 'table' | 'report' | 'insight';
    title: string;
    payload: Record<string, unknown>;
  }) => void;
  onSaveUdq?: (input: {
    intent: string;
    filters?: Record<string, unknown>;
    query?: string;
    title?: string;
    displayColumns?: string[];
  }) => void;
  onFeedback?: (input: { messageId: string; rating: 'helpful' | 'not_helpful'; assistantText: string }) => void;
}

export function AgentMessages({ messages, emptyHint, onActionPrompt, onPin, onSaveUdq, onFeedback }: AgentMessagesProps) {
  if (messages.length === 0) {
    return (
      <Text type="secondary" style={{ display: 'block', padding: '8px 0' }}>
        {emptyHint ?? 'Ask in plain language, and I will answer using your live ERP data.'}
      </Text>
    );
  }
  const ordered = orderMessagesForDisplay(messages);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {ordered.map((m) => {
        if (m.role === 'user') {
          return (
            <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <UserOutlined style={{ marginTop: 4, color: '#145A54' }} />
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{m.text}</Paragraph>
            </div>
          );
        }
        if (m.role === 'assistant') {
          return (
            <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <RobotOutlined style={{ marginTop: 4, color: '#722ed1' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                {m.memoryCitations?.length ? <MemoryReasonTags items={m.memoryCitations} /> : null}
                <AssistantReplyBody text={m.text || ''} />
                {onFeedback && m.text.trim() ? (
                  <Space size={4} style={{ marginTop: 6 }}>
                    <Button
                      type="text"
                      size="small"
                      icon={<LikeOutlined />}
                      aria-label="Helpful"
                      onClick={() => onFeedback({ messageId: m.id, rating: 'helpful', assistantText: m.text })}
                    />
                    <Button
                      type="text"
                      size="small"
                      icon={<DislikeOutlined />}
                      aria-label="Not helpful"
                      onClick={() => onFeedback({ messageId: m.id, rating: 'not_helpful', assistantText: m.text })}
                    />
                  </Space>
                ) : null}
              </div>
            </div>
          );
        }
        if (m.role === 'error') {
          return (
            <Alert
              key={m.id}
              type="error"
              showIcon
              message={m.text}
            />
          );
        }
        return (
          <ToolResultCard
            key={m.id}
            toolName={m.toolName}
            data={m.data}
            compact
            onActionPrompt={onActionPrompt}
            onPin={onPin}
            onSaveUdq={onSaveUdq}
          />
        );
      })}
    </Space>
  );
}
